<div class="flex flex-col max-h-[75dvh] md:max-h-[85vh] overflow-hidden relative">
  <!-- Dialog Title -->
  <ng-template vsfDialogTitle>{{ title || 'Customize' }}</ng-template>

  <div
    *ngIf="isImageProcessing || isProcessingSubmit"
    class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-[60]"
  >
    <div class="flex flex-col items-center gap-3 px-4 py-3 rounded-md border bg-white shadow-lg">
      <fa-icon icon="spinner" class="animate-spin text-2xl text-gray-700"></fa-icon>
      <p class="text-sm text-gray-700 text-center">
        {{ isProcessingSubmit ? 'Finalizing your design…' : 'Processing your photo… This may take a few seconds.' }}
      </p>
    </div>
  </div>
  <div #modalScroll class="flex-1 flex flex-col md:flex-row md:gap-4 min-h-0 overflow-y-auto"
      cdkScrollable>
    <!-- Error -->
    <div *ngIf="!printingPattern" class="p-2 text-center text-red-600">
      Error: Configuration pattern not loaded correctly.
    </div>

    <!-- Main Panels -->
    <div *ngIf="printingPattern" class="flex-1 flex flex-col">
      <!-- Desktop (md+): fixed-height row with two independently-scrolling
           columns. Mobile: natural height so the OUTER container scrolls the
           whole modal as one — `h-full`/`min-h-0` are gated to md: because on
           mobile they pin the body to the viewport and (with no inner scroll)
           make the tall content unreachable. -->
      <div class="flex flex-col md:flex-row md:gap-2 md:h-full md:min-h-0">
        <!-- Canvas Section (Left) -->
        <div class="md:flex-1 md:min-h-0 p-2 md:overflow-y-auto">
          <div class="font-bold text-base md:text-lg flex-1 w-full">
            <span class="block truncate" [title]="variantName">{{ variantName }}</span>
          </div>
          <div class="canvas-container-wrapper relative w-full">
            <div #canvasContainer class="canvas-inner-container relative max-w-full max-h-full block">
              <canvas #editorCanvas id="editorCanvasModal" class="border"></canvas>
            </div>
          </div>
          <p class="text-sm text-gray-600 mt-2 text-center">
            Note: Display is an approximate preview.
          </p>
        </div>

        <!-- Upload Panel (Right). Same responsive gating as the canvas panel. -->
        <div class="md:flex-1 md:min-h-0 md:overflow-y-auto md:pl-2"
            cdkScrollable>
          <div class="controls-area w-full max-w-xl mx-auto flex flex-col gap-3 items-center px-2 bg-gray-100">
            <!-- Pattern label/description -->
            <div class="flex flex-col items-start w-full">
              <strong *ngIf="printingPattern.label" class="text-lg font-bold">{{ printingPattern.label }}</strong>
              <p *ngIf="printingPattern.description" class="text-sm text-gray-600 mb-2 text-left" [innerHtml]="printingPattern.description | safeHtml"></p>
            </div>

            <!-- Background Image Switcher -->
            <div class="flex gap-2 flex-wrap w-full" *ngIf="printingPattern?.backgroundImages?.length > 1">
              <div class="mb-2 border-b bg-white w-full rounded-md p-2">
                <h3 class="text-lg font-bold mb-2">Select a background image</h3>
                <ng-container [ngSwitch]="printingPattern.backgroundImageDisplayType">
                  <!-- Dropdown -->
                  <div *ngSwitchCase="'name-list'" class="relative w-full mb-2">
                    <vsf-dropdown #bgDd [matchTriggerWidth]="true" [closeOnDocumentClick]="true">
                      <button
                        vsfDropdownTrigger
                        type="button"
                        class="form-control w-full flex justify-between items-center">
                        <div class="flex items-center">
                          <img [src]="printingPattern.backgroundImages[selectedBackgroundIndex]?.asset | assetPreview:'thumb':'webp'" alt=""
                            class="w-8 h-8 object-cover rounded mr-2 pointer-events-none"/>
                          <span class="block w-full text-center whitespace-nowrap overflow-hidden text-ellipsis"
                            [title]="printingPattern.backgroundImages[selectedBackgroundIndex]?.label">
                            {{ printingPattern.backgroundImages[selectedBackgroundIndex]?.label }}
                          </span>
                        </div>
                        <fa-icon icon="chevron-down"></fa-icon>
                      </button>
                      <div vsfDropdownContent>
                        <ul class="max-h-60 overflow-auto bg-white divide-y divide-gray-100">
                          <li *ngFor="let bg of printingPattern.backgroundImages; let i = index"
                            (click)="onBackgroundChange(i); bgDd.close()"
                            class="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-100"
                            [class.font-semibold]="i === selectedBackgroundIndex">
                            <img [src]="bg.asset | assetPreview:'thumb':'webp'" alt="" class="w-8 h-8 object-cover rounded mr-2 pointer-events-none">
                            <span class="block w-full whitespace-nowrap overflow-hidden text-ellipsis" [title]="bg.label">{{ bg.label }}</span>
                          </li>
                        </ul>
                      </div>
                    </vsf-dropdown>
                  </div>
                  <!-- Tiles -->
                  <div *ngSwitchCase="'image-tiles'" class="flex flex-wrap gap-2">
                    <div *ngFor="let bg of printingPattern.backgroundImages; let i = index"
                      class="cursor-pointer border rounded-md m-2 w-20 flex flex-col items-center justify-start"
                      [ngClass]="{'ring-2 ring-primary-500': i === selectedBackgroundIndex}"
                      (click)="onBackgroundChange(i)">
                      <img *ngIf="(bg.asset?.imageUrl || bg.asset?.preview); else emptyTile" [src]="bg.asset | assetPreview:'thumb':'webp'" class="h-16 w-16 object-cover mb-1 pointer-events-none" [alt]="bg.label">
                      <ng-template #emptyTile>
                        <div class="h-16 w-16 flex items-center justify-center bg-gray-100 text-gray-400">
                          <fa-icon icon="image" class="text-2xl"></fa-icon>
                        </div>
                      </ng-template>
                      <span class="block w-full text-xs text-center whitespace-nowrap overflow-hidden text-ellipsis" [title]="bg.label">{{ bg.label }}</span>
                    </div>
                  </div>
                </ng-container>
              </div>
            </div>

            <!-- Image Uploads (Repeatable for multiple slots) -->
            <div class="flex gap-3 flex-wrap pb-3 w-full">
              <div *ngFor="let idx of imageButtonsIndices" class="flex flex-col items-start w-full">
                <div class="mb-2 border-b bg-white w-full rounded-md p-2">
                  <label class="font-medium">
                    <strong class="text-red-500">*</strong>
                    {{ frameStates[idx].name }}
                  </label>
                  <p class="text-sm text-gray-600 mb-2">
                    Upload a file no larger than {{maxImageSizeMB}}MB in JPEG or PNG format.
                  </p>
                  <p *ngIf="printingPattern.minWidth && printingPattern.minHeight" class="text-sm text-gray-600 mb-2">
                    Suggested min resolution is {{ printingPattern.minWidth }} x {{ printingPattern.minHeight }}
                  </p>
                  <div class="flex flex-col items-start mb-2">
                    <span *ngIf="frameStates[idx]?.originalImageDataUrl"
                      class="mb-1 text-sm font-bold">
                      {{ frameStates[idx]?.fileName }}
                    </span>
                    <div class="flex flex-row items-center gap-2">
                      <div *ngIf="frameStates[idx]?.thumbnailUploadUrl; else photoPlaceholder">
                        <img [src]="frameStates[idx]?.thumbnailUploadUrl"
                          alt="Frame {{ idx + 1 }} Thumbnail"
                          class="w-16 h-16 object-cover border border-gray-300"/>
                      </div>
                      <ng-template #photoPlaceholder>
                        <div class="flex items-center justify-center w-16 h-16 border
                                 border-gray-300 bg-gray-100">
                          <fa-icon [icon]="['fas', 'image']" class="text-gray-500 text-4xl"></fa-icon>
                        </div>
                      </ng-template>

                      <div class="flex flex-row items-center gap-2">
                        <label [for]="'imageUploadModal' + idx"
                          class="btn btn-sm btn-secondary flex flex-col items-center justify-center
                                 w-16 h-16 px-2 py-1 text-center"
                          [class.disabled]="frameStates[idx]?.isLoading || isProcessingSubmit"
                          [attr.aria-disabled]="frameStates[idx]?.isLoading || isProcessingSubmit">
                          <fa-icon icon="upload" class="text-xl mb-1"></fa-icon>
                          <span class="text-xs">
                            {{ frameStates[idx]?.originalImageDataUrl ? 'Change' : 'Upload' }}
                          </span>
                        </label>

                        <button *ngIf="frameStates[idx]?.originalImageDataUrl"
                          type="button"
                          (click)="deleteImage(idx)"
                          class="btn btn-sm btn-secondary flex flex-col items-center
                                 justify-center w-16 h-16 px-2 py-1 text-center">
                          <fa-icon icon="trash" class="text-xl mb-1"></fa-icon>
                          <span class="text-xs">Delete</span>
                        </button>

                        <input type="file"
                          [id]="'imageUploadModal' + idx"
                          accept="image/jpg, image/jpeg, image/png"
                          (change)="uploadImage($event, idx)"
                          [disabled]="frameStates[idx]?.isLoading || isProcessingSubmit"
                          class="hidden"/>
                      </div>
                    </div>
                  </div>
                  <p *ngIf="validationWarnings[idx]" class="text-xs text-red-500 mt-1">
                    Please upload a photo for this position.
                  </p>
                </div>
                <hr>
              </div>
            </div>

            <!-- Shared per-group card body, instantiated for both nested and
                 standalone overlays via *ngTemplateOutlet. Declared before its
                 first use so the outlet reference resolves on the initial render.
                 `gi` is the original index into textGroupStates so the change
                 handlers stay correct. -->
            <ng-template #groupCard let-gs="gs" let-gi="gi">
              <div class="flex flex-col items-start w-full">
                <vsf-card class="mb-3 p-3 w-full bg-white border rounded-md">
                  <label class="font-medium text-lg">{{ gs.config.name }}</label>

                  <!-- Font selector -->
                  <div class="mt-2">
                    <label class="text-sm text-gray-600 block mb-1">Font</label>
                    <select
                      class="form-control w-full"
                      [ngModel]="gs.selectedFont"
                      (ngModelChange)="onGroupFontChange(gi, $event)"
                      [disabled]="isProcessingSubmit">
                      <option *ngFor="let f of gs.effectiveFonts" [value]="f.family"
                        [style.font-family]="f.family">{{ f.family }}</option>
                    </select>
                  </div>

                  <!-- Color swatches -->
                  <div *ngIf="gs.config.allowedColors.length" class="mt-2">
                    <label class="text-sm text-gray-600 block mb-1">Color: {{ gs.selectedColorName }}</label>
                    <div class="flex flex-wrap gap-2">
                      <button *ngFor="let c of gs.config.allowedColors" type="button"
                        class="w-8 h-8 rounded-full border-2 transition-all"
                        [style.background-color]="c.hex"
                        [title]="c.name || c.hex"
                        [disabled]="isProcessingSubmit"
                        [ngClass]="{'ring-2 ring-offset-1 ring-primary-500 border-primary-500': c.hex === gs.selectedColor, 'border-gray-300': c.hex !== gs.selectedColor}"
                        (click)="onGroupColorChange(gi, c)">
                      </button>
                    </div>
                  </div>

                  <!-- Font size buttons (when not autoFit) -->
                  <div *ngIf="!gs.config.autoFit && gs.config.fontSizeOptions.length" class="mt-2">
                    <label class="text-sm text-gray-600 block mb-1">Font Size</label>
                    <div class="flex flex-wrap gap-1">
                      <button *ngFor="let sz of gs.config.fontSizeOptions" type="button"
                        class="px-3 py-1 text-sm border rounded transition-all"
                        [disabled]="isProcessingSubmit"
                        [ngClass]="{'bg-primary-500 text-white border-primary-500': sz === gs.selectedFontSize, 'bg-white text-gray-700 border-gray-300 hover:border-gray-400': sz !== gs.selectedFontSize}"
                        (click)="onGroupFontSizeChange(gi, sz)">
                        {{ sz }}px
                      </button>
                    </div>
                  </div>

                  <!-- Per-frame text inputs -->
                  <div *ngFor="let fs of gs.frameStates; let fi = index" class="mt-3">
                    <label class="text-sm font-medium block mb-1">
                      <strong *ngIf="fs.frameData.required" class="text-red-500">*</strong>
                      {{ fs.frameData.name }}
                    </label>
                    <div class="flex flex-col gap-1">
                      <textarea
                        [id]="'textFrame_' + gi + '_' + fi"
                        class="form-control w-full resize-none"
                        rows="3"
                        [maxlength]="fs.frameData.maxLength"
                        [placeholder]="fs.frameData.label || ''"
                        [ngModel]="fs.content"
                        (ngModelChange)="onTextContentChange(gi, fi, $event)"
                        [disabled]="isProcessingSubmit"></textarea>
                      <div class="flex flex-col gap-0.5">
                        <div class="flex justify-between text-xs">
                          <p *ngIf="!fs.isValid" class="text-red-500">This field is required.</p>
                          <span class="ml-auto"
                            [ngClass]="{'text-red-500': fs.content.length >= fs.frameData.maxLength, 'text-gray-500': fs.content.length < fs.frameData.maxLength}">
                            {{ fs.content.length }} / {{ fs.frameData.maxLength }}
                          </span>
                        </div>
                        <p *ngIf="fs.isOverLineLimit" class="text-xs text-orange-500">
                          Maximum {{ fs.frameData.maxLines || 3 }} lines allowed. Only the first {{ fs.frameData.maxLines || 3 }} lines will be displayed.
                        </p>
                      </div>
                    </div>
                  </div>
                </vsf-card>
              </div>
            </ng-template>

            <!-- Image-frame nested text-overlays — rendered immediately after the
                 uploader section so each frame's overlay controls sit next to its
                 upload. No per-frame subheading: each group card already carries a
                 frame-scoped name (e.g. "Photo 1 Text Overlay"). Shares #groupCard. -->
            <div *ngIf="nestedTextGroupEntries.length" class="text-frame-groups flex gap-3 flex-wrap pb-3 w-full">
              <ng-container *ngFor="let entry of nestedTextGroupEntries; trackBy: trackTextGroupEntry">
                <ng-container *ngTemplateOutlet="groupCard; context: entry"></ng-container>
              </ng-container>
            </div>

            <!-- Ordered Non-Print Options — message / paid / standalone overlay
                 groups interleaved per config order, flattened into a single
                 `renderItems` list so one option's re-render can't disturb the
                 others (see component note). Image-frame nested overlays render
                 earlier, right after the uploader section. -->
            <div *ngIf="renderItems.length" class="flex gap-3 flex-wrap pb-3 w-full">
              <ng-container *ngFor="let entry of renderItems">

                <!-- Paid Option -->
                <ng-container *ngIf="entry.kind === 'paid'">
                  <div class="flex flex-col items-start w-full">
                    <div class="mb-3 p-3 w-full bg-white rounded-md p-2">
                      <label class="font-medium">
                        <strong *ngIf="paidOptions[entry.index].required" class="text-red-500">*</strong>
                        {{ paidOptions[entry.index].label }}
                      </label>
                      <p class="text-xs text-gray-600 mb-2" [innerHtml]="paidOptions[entry.index].description | safeHtml"></p>
                      <ng-container [ngSwitch]="paidOptions[entry.index].displayType">
                        <ng-container *ngSwitchCase="'option-list'" >
                          <!-- Selecting an option-list item toggles box-shadow
                               (ring-2) + font-weight on a deeply-nested node;
                               inside the dialog's transformed CDK overlay Chromium
                               can leave the composited modal body unpainted (blank)
                               until a reflow. Worked around by nudgeRepaint() in
                               the component (called from onPaidChange). image-tiles
                               / drop-down-list don't hit it. -->
                          <div class="rounded-lg bg-white shadow border">
                            <ul role="list" class="divide-y divide-gray-200">
                              <li *ngFor="let item of paidOptions[entry.index].options" class="px-4 py-2">
                                <label
                                  class="border rounded-md p-3 cursor-pointer flex items-center hover:ring-2 hover:ring-primary-500 hover:font-semibold"
                                  (click)="onPaidChange(entry.index, item)"
                                  [ngClass]="{
                                    'ring-2 ring-primary-500 font-semibold': item === paidSelections[entry.index],
                                    'gap-x-3': paidOptions[entry.index].showImage
                                  }">
                                  <input
                                    type="radio"
                                    class="absolute opacity-0"
                                    [name]="'paidOpt' + entry.index"
                                    [value]="item"
                                    [checked]="item === paidSelections[entry.index]" />
                                  <ng-container *ngIf="paidOptions[entry.index].showImage">
                                    <img *ngIf="(item.asset?.imageUrl || item.asset?.preview); else noThumb"
                                        [src]="item.asset | assetPreview:'thumb':'webp'"
                                        class="w-12 h-12 object-cover rounded pointer-events-none" />
                                    <ng-template #noThumb>
                                      <div class="w-12 h-12 flex items-center justify-center bg-gray-100
                                                  text-gray-400 rounded pointer-events-none">
                                        <fa-icon icon="image"></fa-icon>
                                      </div>
                                    </ng-template>
                                  </ng-container>
                                  <div class="flex-1 flex items-center justify-between">
                                    <span class="truncate">{{ item.label }}</span>
                                    <span *ngIf="item.price" class="ml-4 text-primary-600 font-semibold">
                                      + {{ item.price | formatPrice }}
                                    </span>
                                  </div>
                                </label>
                              </li>
                            </ul>
                          </div>
                        </ng-container>
                        <ng-container *ngSwitchCase="'drop-down-list'" >
                          <div #dropdownWrap class="w-full">
                            <vsf-dropdown #poDd [matchTriggerWidth]="true" [closeOnDocumentClick]="true">
                              <button vsfDropdownTrigger #triggerBtn type="button" class="form-control w-full flex justify-between border items-center h-16 px-4 py-2 text-base">
                                <div class="flex items-center w-full mr-2">
                                  <ng-container *ngIf="paidOptions[entry.index].showImage">
                                    <img *ngIf="(paidSelections[entry.index]?.asset?.imageUrl || paidSelections[entry.index]?.asset?.previeww); else emptyImage" [src]="paidSelections[entry.index]?.asset | assetPreview:'thumb':'webp'" alt="" class="w-12 h-12 object-cover rounded mr-2 pointer-events-none" />
                                    <ng-template #emptyImage>
                                      <div class="h-12 w-12 flex items-center justify-center bg-gray-100 text-gray-400">
                                        <fa-icon icon="image" class="text-xl"></fa-icon>
                                      </div>
                                    </ng-template>
                                  </ng-container>
                                  <div class="flex-1 flex items-center justify-between w-full">
                                    <span class="truncate">{{ paidSelections[entry.index]?.label || 'Select…' }}</span>
                                    <span *ngIf="paidSelections[entry.index]?.price" class="ml-4 text-primary-600 font-semibold">
                                      + {{ paidSelections[entry.index]?.price | formatPrice }}
                                    </span>
                                  </div>
                                </div>
                                <fa-icon icon="chevron-down"></fa-icon>
                              </button>
                              <div vsfDropdownContent class="w-full">
                                <ul class="max-h-72 overflow-auto bg-white divide-y divide-gray-100 w-full">
                                  <li
                                    *ngFor="let item of paidOptions[entry.index].options"
                                    (click)="onPaidChange(entry.index, item); poDd.close()"
                                    class="px-4 py-2 hover:bg-gray-50 cursor-pointer w-full"
                                    [class.font-semibold]="item === paidSelections[entry.index]">
                                    <label class="flex flex-row items-center w-full">
                                      <ng-container *ngIf="paidOptions[entry.index].showImage">
                                        <img
                                          *ngIf="(item.asset?.imageUrl || item.asset?.preview); else noImg"
                                          [src]="item.asset | assetPreview:'thumb':'webp'"
                                          class="w-12 h-12 object-cover rounded mr-2 flex-shrink-0" />
                                        <ng-template #noImg>
                                          <div
                                            class="w-12 h-12 flex items-center justify-center
                                                  bg-gray-100 text-gray-400 rounded mr-2 flex-shrink-0">
                                            <fa-icon icon="image"></fa-icon>
                                          </div>
                                        </ng-template>
                                      </ng-container>
                                      <div class="flex-1 flex items-center justify-between">
                                        <span class="truncate flex-1">{{ item.label }}</span>
                                        <span *ngIf="item.price" class="ml-auto flex-shrink-0 text-primary-600 font-semibold">
                                          + {{ item.price | formatPrice }}
                                        </span>
                                      </div>
                                      <input
                                        type="radio"
                                        class="absolute opacity-0"
                                        [name]="'paidOpt' + entry.index"
                                        [value]="item"
                                        [checked]="item === paidSelections[entry.index]" />
                                    </label>
                                  </li>
                                </ul>
                              </div>
                            </vsf-dropdown>
                          </div>
                        </ng-container>
                        <ng-container *ngSwitchCase="'image-tiles'">
                          <div class="flex flex-wrap gap-3">
                            <div
                              *ngFor="let item of paidOptions[entry.index].options"
                              class="cursor-pointer border rounded-md w-24 flex flex-col items-center p-2
                                      hover:ring-2 hover:ring-primary-500"
                              (click)="onPaidChange(entry.index, item)"
                              [ngClass]="{ 'ring-2 ring-primary-500': item === paidSelections[entry.index] }">
                              <img
                                *ngIf="(item.asset?.imageUrl || item.asset?.preview); else noImg"
                                [src]="item.asset | assetPreview:'thumb':'webp'"
                                class="h-16 w-16 object-cover rounded pointer-events-none" />
                              <ng-template #noImg>
                                <div
                                  class="h-16 w-16 flex items-center justify-center bg-gray-100
                                          text-gray-400 rounded pointer-events-none">
                                  <fa-icon icon="image" class="text-2xl"></fa-icon>
                                </div>
                              </ng-template>
                              <span
                                class="mt-1 block w-full truncate text-center text-xs"
                                [title]="item.label">
                                {{ item.label }}
                              </span>
                              <span *ngIf="item.price"
                                class="text-xs font-semibold text-primary-600">
                                + {{ item.price | formatPrice }}
                              </span>
                            </div>
                          </div>
                          <p
                            *ngIf="paidOptions[entry.index].note"
                            class="mt-3 text-xs text-gray-600"
                            [innerHtml]="paidOptions[entry.index].note | safeHtml">
                          </p>
                        </ng-container>
                      </ng-container>
                      <p
                        *ngIf="paidSelections[entry.index]?.description"
                        class="mt-2 text-base text-gray-600"
                        [innerHtml]="paidSelections[entry.index]?.description | safeHtml">
                      </p>
                      <p *ngIf="paidValidation[entry.index]" class="text-xs text-red-500 mt-1">Please choose an option.</p>
                    </div>
                  </div>
                </ng-container>

                <!-- Message Option -->
                <ng-container *ngIf="entry.kind === 'message'">
                  <div class="mb-3 p-3 w-full bg-white border rounded-md p-2">
                    <label class="font-medium">
                      <strong *ngIf="messageOptions[entry.index].required" class="text-red-500">*</strong>
                      {{ messageOptions[entry.index].label }}
                    </label>
                    <p
                      class="text-xs text-gray-600 mb-2"
                      [innerHtml]="messageOptions[entry.index].description | safeHtml">
                    </p>
                    <div class="flex flex-col gap-1">
                      <textarea
                        class="form-control w-full"
                        rows="3"
                        [maxlength]="maxNotesLength"
                        [(ngModel)]="messageInputs[entry.index]"
                        (ngModelChange)="onMessageChange(entry.index, $event)"
                        [disabled]="isProcessingSubmit">
                      </textarea>
                      <div
                        class="text-xs text-right"
                        [ngClass]="{
                          'text-gray-500': (messageInputs[entry.index]?.length || 0) <= maxNotesLength,
                          'text-red-500': (messageInputs[entry.index]?.length || 0) >  maxNotesLength
                        }">
                        {{ (messageInputs[entry.index]?.length || 0) }} / {{ maxNotesLength }}
                      </div>
                      <p *ngIf="messageValidation[entry.index]" class="text-xs text-red-500">
                        Please enter a message.
                      </p>
                    </div>
                  </div>
                </ng-container>

                <!-- Standalone text-overlay group (custom-text-frames) rendered
                     at its configured position. `showSubheading` is set on the
                     first group of each option so the label/description divider
                     appears once per option. The render item doubles as the
                     groupCard context (stable {gs, gi} — no per-pass literal). -->
                <ng-container *ngIf="entry.kind === 'overlay'">
                  <div *ngIf="entry.showSubheading"
                    class="frame-subheading free-floating-subheading w-full pt-3 border-t border-gray-200">
                    <h3 class="font-semibold text-base text-gray-800">
                      {{ entry.subheadingLabel }}
                    </h3>
                    <p *ngIf="entry.subheadingDescription" class="text-sm text-gray-600">
                      {{ entry.subheadingDescription }}
                    </p>
                  </div>
                  <ng-container *ngTemplateOutlet="groupCard; context: entry"></ng-container>
                </ng-container>

              </ng-container>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Sticky Dialog Footer -->
  <ng-template vsfDialogButtons class="sticky bottom-0 bg-white z-[5]">
    <hr class="md:my-2">
    <div *ngIf="missingImagesCount > 0" class="text-center md:mb-2 px-4">
      <p class="text-red-500 text-sm">
        Please upload all required photos before continuing.
        <span *ngIf="missingImagesCount > 1">{{missingImagesCount}} photos are missing.</span>
        <span *ngIf="missingImagesCount === 1">1 photo is missing.</span>
      </p>
    </div>
    <div *ngIf="uploadMergedCanvasFailed" class="text-center md:mb-2 px-4">
      <p class="text-red-500 text-sm">Something wrong with the uploading process. Please try it again.</p>
    </div>
    <div class="flex justify-between items-center gap-3 px-4 mb-4 md:mb-0 pb-6 py-2 md:py-6 sticky bottom-0 bg-white shadow z-[5]">
      <span class="font-bold text-base md:text-2xl">Total:&nbsp;{{ totalPrice | formatPrice }}</span>
      <div class="flex gap-3">
        <button type="button" class="btn text-sm md:text-base btn-secondary py-1" (click)="cancel()" [disabled]="isProcessingSubmit">Cancel</button>
        <button type="button" class="btn text-sm md:text-base btn-primary py-1" (click)="submit()" [disabled]="!canSubmit()">
          <span *ngIf="!isProcessingSubmit">{{buttonText}}</span>
          <span *ngIf="isProcessingSubmit">Processing…</span>
        </button>
      </div>
    </div>
  </ng-template>
</div>
